/**
 * Pricing Consistency Validator
 * Ensures pricingConfig.js and PricingRegistry.js maintain the same structure
 * Run this to verify all three components are in sync
 */

import { validateStructure, getCategories } from './pricingStructure';

import PricingRegistry from './PricingRegistry';
import { DEFAULT_RULES } from './PricingRules';
import { DEFAULT_PRICES, DEFAULT_PROFITS } from './pricingConfig';

export class PricingConsistencyValidator {
  constructor({
    ruleConfig = DEFAULT_RULES,
    priceConfig = DEFAULT_PRICES,
    profitConfig = DEFAULT_PROFITS,
  }) {
    this.ruleConfig = ruleConfig;
    this.priceConfig = priceConfig;
    this.profitConfig = profitConfig;
  }
  /**
   * Validate all pricing configurations are consistent
   * @throws {Error} If any inconsistency found
   * @returns {object} Validation report
   */
  validateAll() {
    const report = {
      timestamp: new Date().toISOString(),
      checks: [],
      errors: [],
      warnings: [],
    };

    try {
      report.checks.push(this.validatePrices());
      report.checks.push(this.validateProfits());
      report.checks.push(this.validateRegistry());
    } catch (error) {
      report.errors.push(error.message);
    }

    return report;
  }

  /**
   * Validate this.priceConfig structure
   * @returns {object} Check result
   */
  validatePrices() {
    const check = {
      name: 'this.priceConfig',
      status: 'pending',
      message: '',
    };

    try {
      validateStructure(this.priceConfig, 'this.priceConfig');
      check.status = 'pass';
      check.message = 'All required keys present';
    } catch (error) {
      check.status = 'fail';
      check.message = error.message;
    }

    return check;
  }

  /**
   * Validate this.profitConfig structure
   * @returns {object} Check result
   */
  validateProfits() {
    const check = {
      name: 'this.profitConfig',
      status: 'pending',
      message: '',
    };

    try {
      validateStructure(this.profitConfig, 'this.profitConfig');
      check.status = 'pass';
      check.message = 'All required keys present';
    } catch (error) {
      check.status = 'fail';
      check.message = error.message;
    }

    return check;
  }

  /**
   * Validate PricingRegistry.reset() structure
   * @returns {object} Check result
   */
  validateRegistry() {
    const check = {
      name: 'PricingRegistry.reset()',
      status: 'pending',
      message: '',
    };

    try {
      const registry = new PricingRegistry();
      const registryData = registry.getRegistry();
      validateStructure(registryData, 'PricingRegistry');
      check.status = 'pass';
      check.message = 'All required keys present';
    } catch (error) {
      check.status = 'fail';
      check.message = error.message;
    }

    return check;
  }

  /**
   * Validate that PRICES and PROFITS have same keys
   * @returns {object} Check result
   */
  static validatePricesVsProfits() {
    const check = {
      name: 'PRICES vs PROFITS key alignment',
      status: 'pending',
      message: '',
      mismatches: [],
    };

    try {
      const pricesKeys = JSON.stringify(this.priceConfig);
      const profitsKeys = JSON.stringify(this.profitConfig);

      // Compare structure (not values)
      const comparePaths = (prices, profits, path = '') => {
        for (const key in prices) {
          const priceValue = prices[key];
          const profitValue = profits[key];

          if (!(key in profits)) {
            check.mismatches.push(`Missing in PROFITS: ${path}.${key}`);
          }

          if (
            typeof priceValue === 'object' &&
            priceValue !== null &&
            !Array.isArray(priceValue)
          ) {
            if (
              typeof profitValue !== 'object' ||
              profitValue === null ||
              Array.isArray(profitValue)
            ) {
              check.mismatches.push(
                `Type mismatch at ${path}.${key}: PRICES is object, PROFITS is ${typeof profitValue}`
              );
            } else {
              comparePaths(priceValue, profitValue, `${path}.${key}`);
            }
          }
        }
      };

      comparePaths(this.priceConfig, this.profitConfig);

      if (check.mismatches.length === 0) {
        check.status = 'pass';
        check.message = 'All keys aligned';
      } else {
        check.status = 'fail';
        check.message = `Found ${check.mismatches.length} mismatches`;
      }
    } catch (error) {
      check.status = 'fail';
      check.message = error.message;
    }

    return check;
  }

  /**
   * Print validation report to console
   * @param {object} report - Validation report object
   */
  static printReport(report) {
    console.log('\n' + '='.repeat(60));
    console.log('PRICING CONSISTENCY VALIDATION REPORT');
    console.log('='.repeat(60));
    console.log(`Timestamp: ${report.timestamp}\n`);

    if (report.checks.length > 0) {
      console.log('CHECKS:');
      report.checks.forEach(check => {
        const symbol = check.status === 'pass' ? '✓' : '✗';
        console.log(`  ${symbol} ${check.name}: ${check.message}`);

        if (check.mismatches && check.mismatches.length > 0) {
          check.mismatches.forEach(mismatch => {
            console.log(`    - ${mismatch}`);
          });
        }
      });
    }

    if (report.errors.length > 0) {
      console.log('\nERRORS:');
      report.errors.forEach(error => {
        console.log(`  ✗ ${error}`);
      });
    }

    if (report.warnings.length > 0) {
      console.log('\nWARNINGS:');
      report.warnings.forEach(warning => {
        console.log(`  ⚠ ${warning}`);
      });
    }

    const allPass =
      report.checks.every(c => c.status === 'pass') &&
      report.errors.length === 0;

    console.log('\n' + '='.repeat(60));
    console.log(
      allPass ? 'STATUS: ✓ ALL CHECKS PASSED' : 'STATUS: ✗ FAILURES DETECTED'
    );
    console.log('='.repeat(60) + '\n');

    return allPass;
  }
}

export default PricingConsistencyValidator;
